import {
  CreateSliceOptions,
  CaseReducer,
  PayloadAction,
} from '@reduxjs/toolkit';

import { EntityWithErrorState } from '.';
import { AllResponseTypes } from '../api/responses/api-response-types';

const defaultErrorAction = <T extends EntityWithErrorState<unknown>>(
  state: T,
  action: PayloadAction<any>
) => {
  state.error = action.payload.error;
  state.loading = false;
};

const defaultLoadingAction = <T extends EntityWithErrorState<unknown>>(
  state: T
) => {
  state.loading = true;
  state.error = null;
};

const defaultSuccessAction = <T extends EntityWithErrorState<unknown>>(
  state: T,
  action: PayloadAction<AllResponseTypes>,
  processPayload: (action: PayloadAction<AllResponseTypes>['payload']) => any
) => {
  state.entity = processPayload(action.payload);
  state.error = null;
  state.loading = false;
};

interface FetchSliceOptions<T extends EntityWithErrorState<unknown>> {
  sliceName: string;
  initialStatePayload: null | [];
  onError?: (state: T, action: PayloadAction<any>) => void;
  onSuccess?: (
    state: T,
    action: PayloadAction<any>,
    processPayload: (action: PayloadAction<AllResponseTypes>['payload']) => any
  ) => void;
  onLoading?: (state: T) => void;
  processPayload?: (p: AllResponseTypes) => any;
  additionalActions?: { [K: string]: CaseReducer };
}

export const fetchSliceOptions = <T extends EntityWithErrorState<unknown>>({
  sliceName,
  initialStatePayload,
  onError = defaultErrorAction,
  onLoading = defaultLoadingAction,
  onSuccess = defaultSuccessAction,
  processPayload = p => p,
  additionalActions = {},
}: FetchSliceOptions<T>): CreateSliceOptions => {
  return {
    name: sliceName,
    initialState: {
      loading: false,
      error: false,
      entity: initialStatePayload,
    },
    reducers: {
      loading: (state: T) => onLoading(state),
      error: (state: T, action) => onError(state, action),
      success: (state: T, action) => onSuccess(state, action, processPayload),
      ...additionalActions,
    },
  };
};
